/* ------------------------------------------------------------------ */
/*  What's New — changelog data + seen/unseen helpers                  */
/*                                                                     */
/*  Single source of truth for the What's New page, the welcome-screen */
/*  announcement banner, and the sidebar "unread" dot. Add a new entry */
/*  to the TOP of WHATS_NEW for each release; everything downstream    */
/*  (banner copy, dismissal key, unread dot) keys off the latest id.   */
/* ------------------------------------------------------------------ */

export type WhatsNewIcon =
  | 'sparkles'
  | 'identity'
  | 'instructions'
  | 'feedback'
  | 'model';

export interface WhatsNewFeature {
  /** Short, benefit-oriented headline. */
  title: string;
  /** One or two sentences in plain language for end users. */
  description: string;
  icon: WhatsNewIcon;
}

export interface WhatsNewEntry {
  /** Stable, sortable identifier (ISO date). Drives the banner + unread keys. */
  id: string;
  /** Human-readable release date, e.g. "June 9, 2026". */
  date: string;
  /** Release headline (also used as the banner title). */
  title: string;
  /** One-line summary shown in the announcement banner. */
  summary: string;
  features: WhatsNewFeature[];
  /** Optional external "learn more" link (e.g. an official announcement). */
  link?: {
    url: string;
    label: string;
  };
}

/** Newest entry first. */
export const WHATS_NEW: WhatsNewEntry[] = [
  {
    id: '2026-06-30',
    date: 'June 30, 2026',
    title: 'Claude Sonnet 5 is now available',
    summary:
      'Claude Sonnet 5 replaces Sonnet 4.6 — Anthropic’s most capable Sonnet yet, with stronger reasoning, coding, and agentic tool use at the same balanced tier.',
    features: [
      {
        title: 'Claude Sonnet 5',
        description:
          'Now in the model picker for supported tiers, replacing Claude Sonnet 4.6. It follows through on complex, multi-step tasks more reliably and approaches Opus-level quality at a lower cost.',
        icon: 'model',
      },
      {
        title: 'Sharper reasoning, fewer mistakes',
        description:
          'Sonnet 5 reasons in tighter steps and checks its own work, with lower rates of hallucination — so answers, drafts, and analysis come back more accurate and dependable.',
        icon: 'sparkles',
      },
    ],
    link: {
      url: 'https://www.anthropic.com/news/claude-sonnet-5',
      label: 'Read Anthropic’s announcement',
    },
  },
  {
    id: '2026-06-09',
    date: 'June 9, 2026',
    title: 'A more personal, more helpful AI Hub',
    summary:
      'The assistant now knows more about you. You can also set custom instructions to guide all of your interactions, as well as rate AI generated answers with a single click.',
    features: [
      {
        title: 'This “What’s New” page',
        description:
          'Catch up on the latest AI Hub updates any time — just open “What’s New” from the sidebar.',
        icon: 'sparkles',
      },
      {
        title: 'The assistant knows your role',
        description:
          'Responses are now tailored using your name, job title, and division so the assistant’s guidance fits your work at MSCAA.',
        icon: 'identity',
      },
      {
        title: 'Custom instructions',
        description:
          'Set standing preferences — up to 1,000 characters — on your Settings page, and the assistant will follow them in every conversation.',
        icon: 'instructions',
      },
      {
        title: 'Quick thumbs up / thumbs down',
        description:
          'Rate any answer with a single click. Choose thumbs down to add an optional comment and help us improve the AI Hub.',
        icon: 'feedback',
      },
    ],
  },
  {
    id: '2026-05-20',
    date: 'May 20, 2026',
    title: 'Claude Opus 4.8 is now available',
    summary: 'Anthropic’s latest flagship model joins the lineup.',
    features: [
      {
        title: 'Claude Opus 4.8',
        description:
          'Anthropic’s most capable model to date is now available in the model picker for supported tiers.',
        icon: 'model',
      },
    ],
  },
];

/** The most recent release — drives the banner and the unread dot. */
export const LATEST_WHATS_NEW = WHATS_NEW[0];

/** localStorage key recording the last release the user has opened. */
const SEEN_KEY = 'whats-new-seen';

/** localStorage key for dismissing the announcement banner for a release. */
export function bannerDismissedKey(id: string = LATEST_WHATS_NEW.id): string {
  return `whats-new-dismissed:${id}`;
}

/** True when the user has not yet opened the page for the latest release. */
export function hasUnseenWhatsNew(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== LATEST_WHATS_NEW.id;
  } catch {
    return false;
  }
}

/** Record the latest release as seen (clears the unread dot). */
export function markWhatsNewSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, LATEST_WHATS_NEW.id);
  } catch {
    /* ignore storage failures (private mode, etc.) */
  }
}
